/**
 * PATH: packages/email-hub/src/feature/components/WorkflowRoleChip.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-ROLECHIP-001
 * PURPOSE: The counterparty ROLE chip — "who is this sender to us"
 *          (Supplier / Client / Subcontractor / ...) from the V7
 *          workflow-label sidecar (`email.workflow_label`).
 * WHY: W5-V12 — the classifier learned durable sender roles (W3-V8 P1/P2)
 *      but no surface showed them. One quiet chip answers the operator's
 *      first triage question ("who is this?") at a glance.
 * FLOW: email.workflow_label → suppression gate → tinted role pill.
 * SUPPRESSION (first-principles anti-clutter): renders NOTHING when the
 *   role is empty/unknown, AND when the row already answers the question
 *   via a stronger identity badge — a resolved supplier (supplier_id)
 *   already implies role=supplier, a resolved CRM client (client_id)
 *   already implies role=builder_client. Duplicate chips are clutter,
 *   not signal (rule: one identity answer per row).
 * DEPENDENCIES:
 *   - useEmails.types.EmailWorkflowLabel: the sidecar payload shape.
 * RELATED:
 *   - UI-EMAIL-ROW-META-001: the row chip strip this renders into
 *     (roleChip slot, scrolling tail next to companyChip).
 *   - UI-SETTINGS-SENDERS-027: the stewardship surface where a wrong
 *     role gets corrected (Settings → Email → Senders).
 */
import { UserRound } from 'lucide-react'

import { cn } from '../../_internal/lib/utils'
import type { Email } from '../hooks/useEmails.types'

/** Display metadata per learned role — machine key → label + tint.
 *  Mirrors backend `CounterpartyRole` (email_kanban/models/memory.py). */
const ROLE_META: Record<string, { label: string; className: string }> = {
  supplier: {
    label: 'Supplier',
    className:
      'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/20 dark:text-sky-300 dark:border-sky-500/30',
  },
  builder_client: {
    label: 'Client',
    className:
      'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-300 dark:border-emerald-500/30',
  },
  subcontractor: {
    label: 'Subcontractor',
    className:
      'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-300 dark:border-violet-500/30',
  },
  internal_team: {
    label: 'Team',
    className:
      'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800/60 dark:text-slate-300 dark:border-slate-600/40',
  },
  authority: {
    label: 'Authority',
    className:
      'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-500/30',
  },
  saas_vendor: {
    label: 'SaaS vendor',
    className:
      'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-300 dark:border-indigo-500/30',
  },
  recruitment_sender: {
    label: 'Recruitment',
    className:
      'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-500/30',
  },
  finance_lender: {
    label: 'Finance',
    className:
      'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/20 dark:text-teal-300 dark:border-teal-500/30',
  },
}

const SOURCE_LABEL: Record<string, string> = {
  rules: 'deterministic rules',
  llm: 'AI classification',
  b0_fallback: 'legacy classifier',
  profile: 'learned sender profile',
}

/** resolveRoleMeta(email) → chip meta, or null to render nothing.
 *  Pure gate: unknown/empty roles and roles already answered by a
 *  stronger identity badge (supplier/CRM client resolution) are
 *  suppressed. Exported for the story + host tests. */
export function resolveRoleMeta(
  email: Pick<Email, 'workflow_label' | 'supplier_id' | 'client_id'>,
): { role: string; label: string; className: string; title: string } | null {
  const wl = email.workflow_label
  const role = wl?.counterparty_role || ''
  if (!wl || !role || role === 'unknown') return null
  if (role === 'supplier' && email.supplier_id) return null
  if (role === 'builder_client' && email.client_id) return null
  const meta = ROLE_META[role]
  if (!meta) return null
  const pct = Math.round((wl.confidence || 0) * 100)
  const source = SOURCE_LABEL[wl.source] || wl.source || 'classifier'
  const title =
    `${meta.label}: learned counterparty role (${source}` +
    `${pct > 0 ? `, ${pct}% confidence` : ''}). ` +
    'Correct it under Settings > Email > Senders.'
  return { role, label: meta.label, className: meta.className, title }
}

export interface WorkflowRoleChipProps {
  email: Pick<Email, 'workflow_label' | 'supplier_id' | 'client_id'>
  className?: string
}

/** WorkflowRoleChip(email) → one quiet role pill, or null. Purely
 *  informational (no click handler) — corrections happen on the
 *  stewardship surface, not mid-triage. */
export function WorkflowRoleChip({ email, className }: WorkflowRoleChipProps) {
  const meta = resolveRoleMeta(email)
  if (!meta) return null
  return (
    <span
      data-testid="workflow-role-chip"
      data-role={meta.role}
      title={meta.title}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium max-w-[140px]',
        meta.className,
        className,
      )}
    >
      <UserRound className="h-2.5 w-2.5 shrink-0" aria-hidden />
      <span className="truncate">{meta.label}</span>
    </span>
  )
}
